import React, { useState } from "react";
import { ChevronRight, ChevronLeft, Check, Pencil } from "lucide-react";
import { useForm } from "react-hook-form";
import { zodFormResolver } from "@/util/form";
import { z } from "zod";
import { formatNumber } from "@/util/sums";
import { userExistsByEmail } from "@/app/FintreeAPI";
import { usePostRegisterMutation } from "@/store/service";
import { ApiErrorResponse } from "@/store/axiosBaseQuery";
import { useAuth } from "@/context/AuthContext";
import RegisterData from "@/model/interface/RegisterData";
import ColFormValueSlider from "@/components/shared/ColFormValueSlider";
import { isDebugForms } from "@/util/debug";
import { registerPropertySchema, registerUserSchema, registerPasswordSchema } from "@/model/schemas";

const propertySchema = registerPropertySchema.extend({
  savingsForDeposit: z.number().min(1),
  upfrontBoostNeeded: z.number().min(1),
});

const fullSchema = propertySchema.merge(registerUserSchema).merge(registerPasswordSchema);
type UpfrontBoostFormData = z.infer<typeof fullSchema>;

const UpfrontBoostRegister = () => {
  const { refreshAuthStatus } = useAuth();
  const [postRegister] = usePostRegisterMutation();
  const [submitError, setSubmitError] = useState<string | null>(null);

  const [step, setStep] = useState<"property" | "user" | "password" | "success">("property");
  const [estimatedValue, setEstimatedValue] = useState(2500000);
  const [savingsForDeposit, setSavingsForDeposit] = useState(50000);
  const [upfrontBoostNeeded, setUpfrontBoostNeeded] = useState(25000);
  const [emailError, setEmailError] = useState<string | null>(null);

  const maxAmountValue = 20000000;
  const maxAmountSavings = 5000000;
  const [maxBoostNeeded, setMaxBoostNeeded] = useState(150000);
  const [minBoostNeeded, setMinBoostNeeded] = useState(25000);
  const minAmountValue = 500000;
  const minAmountSavings = 10000;

  const {
    register,
    trigger,
    setValue,
    setError,
    clearErrors,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<UpfrontBoostFormData>({
    resolver: zodFormResolver<UpfrontBoostFormData>(fullSchema),
    mode: "onTouched",
    defaultValues: {
      estimatedPropertyValue: 2500000,
      savingsForDeposit: 50000,
      upfrontBoostNeeded: 25000,
    },
  });

  const updateSavingsForDepositDeps = (amount: number) => {
    setMaxBoostNeeded(amount * 3);
    setMinBoostNeeded(amount / 2);
    setUpfrontBoostNeeded(amount);
    setValue("upfrontBoostNeeded", amount);
  };

  const onSubmit = async (data: UpfrontBoostFormData) => {
    setSubmitError(null);
    try {
      await postRegister(data as RegisterData).unwrap();
      await refreshAuthStatus();
      setStep("success");
    } catch (error) {
      setSubmitError((error as Partial<ApiErrorResponse>).detail || "We couldn't create your account. Please try again.");
    }
  };

  const checkEmailExists = async (email: string) => {
    if (!email || !/\S+@\S+\.\S+/.test(email)) return;
    try {
      const response = await userExistsByEmail(email);
      if (response.status === 200) {
        setEmailError("Email already registered.");
        setError("email", { type: "manual", message: "Email already registered." });
      } else {
        setEmailError(null);
        clearErrors("email");
      }
    } catch {}
  };

  const advanceStep = async () => {
    if (step === "property") {
      setValue("estimatedPropertyValue", estimatedValue);
      setValue("savingsForDeposit", savingsForDeposit);
      setValue("upfrontBoostNeeded", upfrontBoostNeeded);
      if (isDebugForms()) {
        setStep("user");
        return;
      }
      const fields = Object.keys(propertySchema.shape) as (keyof typeof propertySchema.shape)[];
      const valid = await trigger(fields);
      if (valid) setStep("user");
    }

    if (step === "user") {
      if (isDebugForms()) {
        setStep("password");
        return;
      }
      const fields = Object.keys(registerUserSchema.shape) as (keyof typeof registerUserSchema.shape)[];
      const valid = await trigger(fields);
      if (valid && !emailError) setStep("password");
    }

    if (step === "password") {
      if (isDebugForms()) {
        setStep("success");
        return;
      }
      const valid = await trigger(["password"]);
      if (valid) handleSubmit(onSubmit)();
    }
  };

  const reverseStep = () => {
    if (step === "user") setStep("property");
    if (step === "password") setStep("user");
  };

  const passwordValue = watch("password") ?? "";

  const inputClass = (hasError: boolean) =>
    `w-full h-11 rounded-lg border bg-white px-4 pr-10 text-sm outline-none transition-colors focus:border-[#0FD131] focus:ring-2 focus:ring-[#9BFBE0] ${hasError ? "border-red-400" : "border-gray-300"}`;

  return (
    <div className="flex flex-col flex-1">
      <section className="relative text-center py-12 text-[#010305]">
        <h2 className="text-3xl font-bold text-gray-900">Upfront Boost</h2>
      </section>
      <section className="flex-1 pb-16">
        <div className="w-full max-w-2xl mx-auto px-4">
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            {step === "property" && (
              <div className="space-y-6">
                <h3 className="text-2xl font-semibold text-gray-900">Property details</h3>

                <ColFormValueSlider
                  title="Estimate the value of the property"
                  step={100000}
                  value={estimatedValue}
                  minAmount={minAmountValue}
                  maxAmount={maxAmountValue}
                  leftColumnValue={estimatedValue}
                  leftColumnFormatFunction={(v) => `$${formatNumber(v, 0, true)}`}
                  onChange={(amount) => setEstimatedValue(amount)}
                />

                <ColFormValueSlider
                  title="Savings for deposit"
                  step={10000}
                  value={savingsForDeposit}
                  minAmount={minAmountSavings}
                  maxAmount={maxAmountSavings}
                  leftColumnValue={savingsForDeposit}
                  leftColumnFormatFunction={(v) => `$${formatNumber(v, 0, true)}`}
                  onChange={(amount) => setSavingsForDeposit(amount)}
                  onFinalChange={(amount) => updateSavingsForDepositDeps(amount)}
                />

                <ColFormValueSlider
                  title="Upfront boost needed"
                  step={1000}
                  value={upfrontBoostNeeded}
                  minAmount={minBoostNeeded}
                  maxAmount={maxBoostNeeded}
                  leftColumnValue={upfrontBoostNeeded}
                  leftColumnFormatFunction={(v) => `$${formatNumber(v, 0, true)}`}
                  onChange={(amount) => setUpfrontBoostNeeded(amount)}
                />

                <div className="relative">
                  <h6 className="text-sm text-gray-500 mb-1">Property address</h6>
                  <input
                    type="text"
                    className={inputClass(!!errors.propertyAddress)}
                    placeholder="Enter property address"
                    {...register("propertyAddress")}
                  />
                  <Pencil className="absolute right-3 top-9 size-3.5 text-gray-400 pointer-events-none" />
                  {errors.propertyAddress && (
                    <p className="mt-1 text-sm text-red-500">{errors.propertyAddress.message}</p>
                  )}
                </div>
              </div>
            )}

            {step === "user" && (
              <div className="space-y-6">
                <h3 className="text-2xl font-semibold text-gray-900">Personal details</h3>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="relative">
                    <h6 className="text-sm text-gray-500 mb-1">First name</h6>
                    <input
                      type="text"
                      {...register("firstName")}
                      className={inputClass(!!errors.firstName)}
                      placeholder="First Name"
                    />
                    <Pencil className="absolute right-3 top-9 size-3.5 text-gray-400 pointer-events-none" />
                    {errors.firstName && <p className="mt-1 text-sm text-red-500">{errors.firstName.message}</p>}
                  </div>
                  <div className="relative">
                    <h6 className="text-sm text-gray-500 mb-1">Last name</h6>
                    <input
                      type="text"
                      {...register("lastName")}
                      className={inputClass(!!errors.lastName)}
                      placeholder="Last Name"
                    />
                    <Pencil className="absolute right-3 top-9 size-3.5 text-gray-400 pointer-events-none" />
                    {errors.lastName && <p className="mt-1 text-sm text-red-500">{errors.lastName.message}</p>}
                  </div>
                </div>

                <div className="relative">
                  <h6 className="text-sm text-gray-500 mb-1">Email</h6>
                  <input
                    type="email"
                    {...register("email", {
                      onBlur: (e) => checkEmailExists(e.target.value),
                    })}
                    className={inputClass(!!errors.email || !!emailError)}
                    placeholder="Email"
                  />
                  <Pencil className="absolute right-3 top-9 size-3.5 text-gray-400 pointer-events-none" />
                  {(errors.email || emailError) && (
                    <p className="mt-1 text-sm text-red-500">{emailError || errors.email?.message}</p>
                  )}
                </div>

                <div className="relative">
                  <h6 className="text-sm text-gray-500 mb-1">Phone</h6>
                  <input type="tel" {...register("phone")} className={inputClass(!!errors.phone)} placeholder="Phone" />
                  <Pencil className="absolute right-3 top-9 size-3.5 text-gray-400 pointer-events-none" />
                  {errors.phone && <p className="mt-1 text-sm text-red-500">{errors.phone.message}</p>}
                </div>
              </div>
            )}

            {step === "password" && (
              <div className="space-y-6">
                <h3 className="text-2xl font-semibold text-gray-900">Set your password</h3>

                <div className="relative">
                  <h6 className="text-sm text-gray-500 mb-1">Password</h6>
                  <input
                    type="password"
                    {...register("password")}
                    autoComplete="new-password"
                    className={inputClass(!!errors.password)}
                    placeholder="Password"
                  />
                  <Pencil className="absolute right-3 top-9 size-3.5 text-gray-400 pointer-events-none" />
                  <ul className="mt-2 space-y-0.5 text-sm">
                    <li className={/[A-Z]/.test(passwordValue) ? "text-green-600" : "text-gray-400"}>
                      {/[A-Z]/.test(passwordValue) && <Check className="inline size-4 mr-1" />}
                      At least one uppercase letter
                    </li>
                    <li className={/[a-z]/.test(passwordValue) ? "text-green-600" : "text-gray-400"}>
                      {/[a-z]/.test(passwordValue) && <Check className="inline size-4 mr-1" />}
                      At least one lowercase letter
                    </li>
                    <li className={/[0-9]/.test(passwordValue) ? "text-green-600" : "text-gray-400"}>
                      {/[0-9]/.test(passwordValue) && <Check className="inline size-4 mr-1" />}
                      At least one number
                    </li>
                    <li className={/\W/.test(passwordValue) ? "text-green-600" : "text-gray-400"}>
                      {/\W/.test(passwordValue) && <Check className="inline size-4 mr-1" />}
                      At least one special character
                    </li>
                    <li className={passwordValue.length >= 8 ? "text-green-600" : "text-gray-400"}>
                      {passwordValue.length >= 8 && <Check className="inline size-4 mr-1" />}
                      At least 8 characters
                    </li>
                  </ul>
                  {submitError && (
                    <p role="alert" className="mt-3 text-sm text-red-600">
                      {submitError}
                    </p>
                  )}
                </div>
              </div>
            )}

            {step === "success" && (
              <div className="space-y-4 text-center">
                <h3 className="text-2xl font-semibold text-gray-900">Success!</h3>
                <p className="text-gray-600">
                  We have securely saved your information. One of our representatives will contact you soon.
                </p>
              </div>
            )}

            {step !== "success" && (
              <div className="flex justify-center gap-4 pt-4">
                {step !== "property" && (
                  <button
                    type="button"
                    onClick={reverseStep}
                    className="inline-flex items-center gap-1 rounded-full bg-white border border-gray-300 px-8 py-3 text-sm font-medium text-[#010305] shadow-sm hover:shadow-md transition-all"
                  >
                    <ChevronLeft className="size-4" /> Back
                  </button>
                )}
                <button
                  type="button"
                  onClick={advanceStep}
                  className="inline-flex items-center gap-1 rounded-full bg-[#9BFBE0] px-8 py-3 text-sm font-medium text-[#010305] shadow-sm hover:shadow-md transition-all"
                >
                  {step !== "password" ? "Next" : "Submit"} <ChevronRight className="size-4" />
                </button>
              </div>
            )}
          </form>
        </div>
      </section>
    </div>
  );
};

export default UpfrontBoostRegister;
